import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

type User = {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
};

type AuthResponse = {
  user: User;
  token: string;
};

export function useAuth() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: user, isLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () =>
      api
        .get<{ user: { userId: string; email: string; isAdmin: boolean } }>("/auth/me")
        .then((r) => ({
          id: r.user.userId,
          email: r.user.email,
          name: r.user.email.split("@")[0], // Fallback name from email
          isAdmin: r.user.isAdmin,
        })),
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      api.post<AuthResponse>("/auth/login", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });

  const signupMutation = useMutation({
    mutationFn: (data: { email: string; password: string; name: string }) =>
      api.post<AuthResponse>("/auth/signup", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.post<{ message: string }>("/auth/logout", {}),
    onSuccess: () => {
      queryClient.clear();
      navigate("/");
    },
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    login: loginMutation.mutate,
    signup: signupMutation.mutate,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isSigningUp: signupMutation.isPending,
    loginError: loginMutation.error,
    signupError: signupMutation.error,
  };
}
